import { InputAdornment } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import { isValidEmail } from '@omnea-app/packages/universal/general/isValidEmail'
import { AssigneeTypeEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { AssigneeEntity } from '@omnea-app/packages/universal/workflow/model/utils/blocks'
import { AssigneeAvatar } from '../avatar/AssigneeAvatar'
import { AssigneeSourceType, useAssigneeOptions } from './AssigneeAutocomplete'
import { AssigneeAutocompleteTextField } from './components/AssigneeAutocompleteTextField'
import { BaseAssigneeAutocomplete, getAssigneeLabel } from './components/BaseAssigneeAutocomplete'
import { AssigneesError } from './types'

interface Props {
  value: AssigneeEntity | null
  onChange: (newValue: AssigneeEntity | null) => void
  title?: string
  label?: string
  required?: boolean
  disabled?: boolean
  additionalOptions?: AssigneeEntity[]
  type?: AssigneeSourceType
  error?: AssigneesError
  fullWidth?: boolean
  placeholder?: string
  onBlur?: (inputValue: string) => void
  helperText?: string
  excludeUserId?: string
  onlyAdditionalOptions?: boolean
  allowedAssignees?: AssigneeEntity[]
  onAddNew?: (inputValue: string) => void
  /** Return `undefined` to hide the add new action for the current input. */
  addNewText?: string | ((inputValue: string) => string | undefined)
  groupBy?: string
  hiddenAssigneeIds?: string[]
  /** Listed but not selectable, unlike `hiddenAssigneeIds`. */
  getOptionDisabled?: (option: AssigneeEntity) => boolean
  clearAfterSelection?: boolean
  minSearchLength?: number
}

export const SingleAssigneeAutocomplete = ({
  type = AssigneeSourceType.USERS_GROUPS,
  value,
  allowedAssignees,
  onlyAdditionalOptions,
  additionalOptions,
  excludeUserId,
  hiddenAssigneeIds,
  getOptionDisabled,
  onChange,
  fullWidth,
  groupBy,
  disabled,
  onAddNew,
  addNewText,
  placeholder,
  title,
  label,
  required,
  onBlur,
  error,
  helperText,
  clearAfterSelection = false,
  minSearchLength,
}: Props) => {
  const [inputValue, setInputValue] = useState(() => (value ? getAssigneeLabel(value) : ''))
  const currentValueIds = value ? [value.id] : []
  const { options } = useAssigneeOptions({
    allowedAssignees,
    onlyAdditionalOptions,
    additionalOptions,
    excludeUserId,
    hiddenAssigneeIds,
    type,
    currentValueIds,
    searchQuery: inputValue,
    minSearchLength,
  })

  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation('workflow-builder')
  const handleChange = (_: unknown, item: AssigneeEntity | string | null) => {
    if (item === null) {
      onChange(null)
      return
    }
    const newValueEntity = typeof item === 'string' ? { value: item, type: AssigneeTypeEnum.EMAIL, id: uuidv4() } : item
    onChange(newValueEntity.type !== AssigneeTypeEnum.EMAIL || isValidEmail(newValueEntity.value) ? newValueEntity : null)

    // Clear input after selection if requested
    if (clearAfterSelection) {
      setInputValue('')
    }
  }

  return (
    <BaseAssigneeAutocomplete<false>
      options={options}
      open={isOpen}
      setIsOpen={setIsOpen}
      multiple={false}
      fullWidth={fullWidth}
      value={value}
      groupBy={groupBy}
      hideGroups={type !== AssigneeSourceType.USERS_GROUPS}
      disabled={disabled}
      getOptionDisabled={getOptionDisabled}
      onChange={handleChange}
      onAddNew={onAddNew ? () => onAddNew(inputValue) : undefined}
      addNewText={typeof addNewText === 'function' ? addNewText(inputValue) : addNewText}
      inputValue={inputValue}
      onInputChange={setInputValue}
      renderInput={(params) => (
        <AssigneeAutocompleteTextField
          {...params}
          label={label}
          required={required}
          placeholder={!value ? placeholder || t('config-assignee-placeholder') : ''}
          startAdornment={
            value ? (
              <InputAdornment position="start">
                <AssigneeAvatar option={value} />
              </InputAdornment>
            ) : undefined
          }
          data-testid={`single-assignee-autocomplete-${title}`}
          onBlur={onBlur ? () => onBlur(inputValue) : undefined}
          error={error}
          helperText={helperText}
          onTabKeyDown={(newValue) => handleChange(null, newValue)}
        />
      )}
    />
  )
}
